// =============================================================================
// scriptMatch — no-AI fuzzy match of a call transcript against the course script
// =============================================================================
// ZERO AI. Pure approximate string matching in the browser. We pull the fixed
// spoken phrases out of the phone-setter scripts (the runs between [PLACEHOLDER]
// slots and sentence ends), then slide each phrase across the call transcript
// and mark the words it covers. A small per-phrase edit budget absorbs the
// whisper garble, so a near-verbatim line still lights up.
//
// Every anchor remembers WHERE it came from (its course file + the phrase text),
// so a highlighted transcript span can teleport the reader to that spot in the
// course (see CourseScenarioPanel `jumpTo`).
//
// What lights up IS near-verbatim course script. A DARK line does NOT prove the
// setter went off-script — the transcript may have mangled it (same trap as our
// reel work: the audio is truth, the transcript lies). So this is a floor on
// script adherence, never a ceiling.
// =============================================================================

import type { WordTime } from "../hooks/useDialProof";

// Filler words that carry no identity on their own. A 4-word phrase made only of
// these is not distinctive enough to be a real script match.
const STOP = new Set(
  "a an and are as at be been but by can could did do for from get got had has have he her him his how i in is it its just like me my no not of on or our out so that the their them then they this to up us was we were what when where which who will with you your".split(
    " "
  )
);

const MIN_ANCHOR_WORDS = 4; // shorter than this over-matches filler
const MIN_CONTENT_WORDS = 2; // must carry ≥2 non-filler words

/** An anchor is one distinctive scripted phrase plus where it lives in the course. */
export interface Anchor {
  /** Normalized token list — what we slide over the transcript. */
  tokens: string[];
  /** Course file the phrase came from (for the teleport). */
  file: string;
  /** Normalized phrase text (tokens joined) — used to locate it in the rendered course. */
  phrase: string;
}

/** Lowercase, drop punctuation, collapse to bare word tokens. Apostrophes become
 *  spaces, so "don't" → "don"+"t" and "link's" → "link"+"s". The edit-distance
 *  matcher then deletes the stray fragment for free, and WEAK marks the fragments
 *  ("t","s","don","won",…) as no-identity so they cannot carry a match alone. */
export const normalizePhrase = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Number words → digits so "twelve months" and "12 months" align (STT writes
// either). Applied to every token, both anchor and transcript.
const NUMW: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
  eleven: "11",
  twelve: "12",
  thirteen: "13",
  fourteen: "14",
  fifteen: "15",
  sixteen: "16",
  seventeen: "17",
  eighteen: "18",
  nineteen: "19",
  twenty: "20",
  thirty: "30",
  forty: "40",
  fifty: "50",
  sixty: "60",
  hundred: "100",
  thousand: "1000",
};
const canon = (t: string): string => NUMW[t] ?? t;

const normTokens = (s: string): string[] =>
  normalizePhrase(s).split(" ").map(canon).filter(Boolean);

// Weak content: bare modals + the "don't" apostrophe-split debris. They pass STOP
// but carry no script identity, so a phrase whose only content is weak ("you
// SHOULD be ABLE to"; "i DON T have time" — a lead objection whose real content is
// just "time") is not a distinctive match. A real match needs ≥2 STRONG (non-stop,
// non-weak) content words. Kept minimal on purpose — a wider list over-trims real
// hits (measured 98 vs 124). Add a fragment here only when a false green proves it.
const WEAK = new Set("should could would able don t".split(" "));

// Per-phrase mismatch budget: exact for short phrases, looser for long ones so
// STT slop on a distinctive 10-word line is still caught.
const budgetFor = (k: number): number => (k >= 8 ? 2 : k >= 5 ? 1 : 0);

// -----------------------------------------------------------------------------
// What counts as SPOKEN SCRIPT (shared by anchor extraction AND the blockquote
// renderer, so both agree). A setter says a line only if:
//   • it carries a dialogue label — YOU:/LEAD:, a voicemail/text label, or an
//     ALL-CAPS name label (SARAH:, JESSICA:) — OR
//   • it is quote-dominant — the quoted words are most of the line, i.e. a
//     standalone example script, NOT a short quote embedded in coaching prose
//     (e.g. `… flat tone, "I don't know." Drop back down …`).
// Everything else is coaching or reference (the priority table, "which doc am I
// in", section explainers) and must never be blockquoted or teleported to.
// -----------------------------------------------------------------------------
const DIALOGUE_LABEL = /^\s*(?:>?\s*)?(?:\*\*)?(?:YOU|LEAD|Voicemail|Voicenote|VM|Text|SMS):/i;
const NAME_LABEL = /^\s*(?:\*\*)?[A-Z]{2,}:/; // SARAH: / JESSICA: — not "Cadence:" (mixed case)

export const isDialogueLabel = (l: string): boolean => DIALOGUE_LABEL.test(l) || NAME_LABEL.test(l);

export const stripDialogueLabels = (l: string): string =>
  l
    .replace(/(?:\*\*)?\b(?:YOU|LEAD|Voicemail|Voicenote|VM|Text|SMS):\**/gi, " ")
    .replace(/^\s*(?:\*\*)?[A-Z]{2,}:/, " ");

export const isTableRow = (l: string): boolean => /^\s*\|.*\|\s*$/.test(l);

// Fraction of a line's non-space characters that sit inside quotes ("…" / “…”).
export const isQuoteDominant = (l: string): boolean => {
  const nonSpace = l.replace(/\s/g, "").length;
  if (!nonSpace) return false;
  const quotes = l.replace(/[“”]/g, '"').match(/"[^"]+"/g);
  if (!quotes) return false;
  const quoted = quotes.reduce((sum, q) => sum + q.replace(/\s/g, "").length, 0);
  return quoted / nonSpace >= 0.6;
};

// Classify each line script (true) or not (false). Headers/blanks/tables/comments
// are never script.
//
// 2026-10-03: NO unlabeled continuation. The Google Docs sync writes every doc
// paragraph as its own line (no blank lines), so "the line after a YOU: turn" is
// always the NEXT paragraph, and that is coaching ("Now you have something…",
// "**Why it works:**", "**If they're registered:**"). The old continuation rule
// pulled 304 such lines into the script bar across the 30 panel docs. A turn that
// really spans lines is a Docs soft break, which stays on one synced line.
// Only a whole-line stage direction "(Pause.)" right after script stays in.
// Also:
//   • "****YOU:" (an empty bold pair before the label) counts as a label.
//   • A list item is never lifted into the bar. Lists stay lists; a quoted
//     example phrase inside a bullet read as one stray barred bullet.
//   • A note TEMPLATE (3+ "CAPS LABEL:" lines, blanks between allowed, 3+
//     different labels: SITUATION / CONTENT / TEMPERATURE…) is a form.
//   • A whole-line bold run is a heading, never a spoken line.
//   • "YOU (screen share again):" counts as a label.
//   • Short option lines after a spoken line ending in ":" stay in the bar.
//   • An unlabeled line after a spoken one stays in only if it reads as speech,
//     see readsSpoken.
const FIELD = /^\s*(?:\*\*)?([A-Z][A-Z'’ ()/&-]{1,40}?)\s*:/;
const isListLine = (l: string): boolean => /^\s*([-*+]|\d+[.)])\s+/.test(l);
// A stage direction is SHORT: "(Pause.)", "(Wait for them to confirm.)". A long
// parenthetical ("(Stay on the line. If it hasn't landed in thirty seconds…)") is
// a coaching note.
// Over 50 characters AND two sentences ("(Surface every concern. Handle each
// one. Keep going.)") is coaching too. "(Pause. Let them answer.)" stays in.
const isDirectionLine = (l: string): boolean =>
  /^\s*\*?\(.*\)\*?\s*$/.test(l) && l.trim().length <= 60 && !(l.trim().length > 50 && /[.!?]\s+\S/.test(l));
// A label that quotes what the LEAD thinks, or a note field, is never a line
// the setter says: "What the prospect is thinking: "…"", "Weak answers: "…"",
// "The big one: "…"", "What you ended with: "…"". Most prose labels ("Ask:",
// "Surface:", "If unsure:", "Right:") DO lead into speech, so this list is closed.
// Measured 2026-10-03: a general prose-label rule pulled 50+ real lines out.
const NOT_SPOKEN_LABEL =
  /^\s*(?:\*\*)?(what (the prospect|they)('s| is| are)? thinking|what their brain hears|weak answers?|the big one|the moment that landed|what you ended with|the core lesson[^:"]*|applied today\. application said)\s*:\s*(?:\*\*)?\s*["“]/i;
const isProseLabelQuote = (l: string): boolean => NOT_SPOKEN_LABEL.test(l);
// A quote that opens on one line and closes lines later (a multi-paragraph
// pitch: '"So based on everything…' … '…walk away with a plan."'). Straight
// quotes only count when odd, so a line holding one whole quote never opens.
const quoteChars = (l: string): number => (l.match(/["“”]/g) ?? []).length;
const opensQuote = (l: string): boolean =>
  /^\s*(?:\*{2,4})?\s*["“]/.test(l) && quoteChars(l) % 2 === 1;
// "YOU (screen share again):" / "Lead (pause):" — a speaker with a delivery cue.
const CUED_LABEL = /^\s*(?:>\s*)?(?:\*\*)?(?:YOU|LEAD|You|Lead|THEM|Them)\s*\([^)]{1,60}\)\s*:/;
// A whole-line bold run is a heading ("**"Just send me the info."**", "**If they're
// registered:**"), never a spoken line, even when it is all quotes.
const isBoldOnly = (l: string): boolean => /^\s*\*\*[^*].*\*\*\s*$/.test(l) && !/\*\*[^*]+\*\*.+\*\*/.test(l.trim().slice(2));

// An unlabeled line after a spoken one (directly, or across ONE blank line) is
// more of that speech only when it READS like speech: a script slot ("[DATE]",
// "[OFFER OWNER]"), a question, first person outside quotes ("I saw you…",
// "we're"), or a talk opener ("So…", "Alright…"). Coaching prose talks to the
// setter about "them" and has none of these. Measured 2026-10-03 on the 30 panel
// docs: 89 lines added, 34 of 35 sampled were real speech.
const SLOT = /\[[A-Z][A-Z0-9 /_.&'-]{1,40}\]/;
const FIRST_PERSON = /\b(I|I'm|I'll|I've|I'd|we|we're|we'll|we've|my|our|me|us)\b/;
const TALK_OPENER = /^\s*(So|And|But|Okay|OK|Alright|Nice|Look|Perfect|Great|Awesome|Cool|Yeah|Honestly|Totally|Ahaha|Hey|Hi)\b/;
const readsSpoken = (l: string): boolean => {
  if (/^\s*(\*\*|\(|[-*+]\s|\d+[.)]\s|#|\|)/.test(l)) return false;
  const outside = l.replace(/[“”]/g, '"').replace(/"[^"]*"/g, "");
  return SLOT.test(l) || /\?\s*["”]?\s*$/.test(l) || FIRST_PERSON.test(outside) || TALK_OPENER.test(outside);
};
// A slot line with a second spoken sign ("Great. I saw you're coming in from
// [LOCATION].") is a script line even with no spoken line above it: the step
// heading sits between it and the last turn.
const standsAsSpeech = (l: string): boolean => {
  if (!SLOT.test(l) || /^\s*(\*\*|\(|[-*+]\s|\d+[.)]\s|#|\|)/.test(l)) return false;
  const outside = l.replace(/[“”]/g, '"').replace(/"[^"]*"/g, "");
  return /\?\s*["”]?\s*$/.test(l) || FIRST_PERSON.test(outside) || TALK_OPENER.test(outside);
};
// No slot, but a talk opener AND first person ("Perfect, grab them and I'll hang
// on."), or a long question to "you" ("Is this something you're doing just for
// yourself…?"). Coaching talks ABOUT the lead in third person.
const opensAsSpeech = (l: string): boolean => {
  if (/^\s*(\*\*|\(|[-*+]\s|\d+[.)]\s|#|\||>)/.test(l) || /:\s*\**\s*$/.test(l)) return false;
  const outside = l.replace(/[“”]/g, '"').replace(/"[^"]*"/g, "");
  if (/\b(they|them|their|the lead|the prospect)\b/i.test(outside)) return false;
  const words = outside.trim().split(/\s+/).length;
  return (TALK_OPENER.test(outside) && FIRST_PERSON.test(outside)) || (words >= 7 && /\byou(r|'re|'ve|'d|'ll)?\b/i.test(outside) && /\?\s*$/.test(l));
};
// Labels that mean a dialogue, never a note template.
const SPEAKER_WORD = /^(YOU|LEAD|THEM|CLOSER|SETTER|PROSPECT|ME)$/;

export function classifyScriptLines(lines: string[]): boolean[] {
  const norm = lines.map((l) => l.replace(/^(\s*(?:>\s*)?)\*{4,}/, "$1"));
  // Mark note-template blocks (blank lines between fields allowed).
  const template = new Array<boolean>(norm.length).fill(false);
  for (let i = 0; i < norm.length; ) {
    if (!FIELD.test(norm[i])) {
      i++;
      continue;
    }
    let j = i;
    let last = i;
    const labels = new Set<string>();
    const hits: number[] = [];
    while (j < norm.length && (FIELD.test(norm[j]) || /^\s*$/.test(norm[j]))) {
      if (FIELD.test(norm[j])) {
        labels.add(FIELD.exec(norm[j])![1].trim());
        hits.push(j);
        last = j;
      }
      j++;
    }
    const dialogue = [...labels].some((x) => SPEAKER_WORD.test(x));
    if (!dialogue && hits.length >= 3 && labels.size >= 3) for (const k of hits) template[k] = true;
    i = last + 1;
  }
  const flags: boolean[] = [];
  let menu = false; // inside short option lines after a spoken line ending in ":"
  let lastSpoken = false; // flag of the last NON-blank line
  let gap = 0; // blank lines since it
  let spanEnd = -1; // last line of an open multi-line quote
  const isBreak = (l: string) =>
    /^\s*(#{1,6}\s|\||\*{0,4}\s*["“])/.test(l) || isTableRow(l) || isDialogueLabel(l) || CUED_LABEL.test(l);
  for (let i = 0; i < norm.length; i++) {
    const l = norm[i];
    const blank = /^\s*$/.test(l);
    let f: boolean;
    let option = false;
    // A quote opening here and closing within 15 lines bars the whole span,
    // blank lines included, so the pitch stays one bar.
    if (i > spanEnd && opensQuote(l) && !template[i]) {
      for (let j = i + 1; j < Math.min(norm.length, i + 16) && !isBreak(norm[j]); j++)
        if (quoteChars(norm[j]) % 2 === 1) {
          if (/["”]\s*\**\s*$/.test(norm[j])) spanEnd = j;
          break;
        }
    }
    if (i <= spanEnd) f = true;
    else if (blank || /^\s*#{1,6}\s/.test(l) || isTableRow(l) || /^\s*<!--/.test(l) || template[i]) f = false;
    else if (isDialogueLabel(l) || CUED_LABEL.test(l)) f = true;
    else if (isBoldOnly(l)) f = false;
    else if (menu && !isListLine(l) && l.trim().length <= 70 && !/[.!]\s*$/.test(l.trim())) f = option = true;
    else if (isListLine(l)) f = false;
    else if (isProseLabelQuote(l)) f = false;
    else if (isQuoteDominant(l)) f = true;
    else if (lastSpoken && gap <= 1 && readsSpoken(l)) f = true;
    else if (standsAsSpeech(l) || opensAsSpeech(l)) f = true;
    else f = isDirectionLine(l) && i > 0 && flags[i - 1];
    // "Is it more:" opens a menu; each short option keeps it open; anything else closes it.
    menu = f && (/:\s*\**\s*$/.test(l) || option);
    if (blank) gap++;
    else {
      lastSpoken = f;
      gap = 0;
    }
    flags.push(f);
  }
  return flags;
}

// -----------------------------------------------------------------------------
// Speaker-aware filtering (closerOnly — the calrev closer course only).
// -----------------------------------------------------------------------------
// The closer course quotes the PROSPECT's example lines too ("I don't know how to
// generate leads", "Yeah, I'm serious"). Those are meant to match the LEAD side of
// a call, so anchoring them lights up a lead's unrelated speech as if it were the
// closer running the script — the #1 precision leak the graders caught 2026-09-18.
// We can't filter by the TRANSCRIPT's speaker (its is_closer diarization is broken),
// but the COURSE side is authored: **YOU:** vs **LEAD:**, "What you say" vs "What
// they said". So we drop the anchors attributed to the prospect at extraction time.
// A quote's speaker comes from the label line ABOVE it (`cue`), reset by a blank
// line or a `#` header; a bold-WRAPPED quote (***"..."*) inherits that cue, never
// resets it from its own words. Phone-setter (dialview) keeps closerOnly = false,
// so its anchor corpus is byte-identical to before.
const LEAD_CUE =
  /\b(lead|prospect|what they said|their internal monologue|internal monologue|what they're thinking|client says|customer says)\b/i;
const CLOSER_CUE = /\b(you say|script|recommended pitch|price drop|the transition|closer)\b/i;
const LEAD_INLINE = /\b(prospect|lead|client says|customer says|they said)\b/i;
const CLOSER_INLINE = /\b(you say|you said|closer)\b/i;
const DIA_LEAD = /^\s*(?:>?\s*)?(?:\*\*)?LEAD:/i; // the prospect's dialogue label
const BOLD_HEADER = /^\s*\*\*/; // a bold header line (**What you say:**) — a cue, not an anchor

const preQuote = (l: string): string => {
  const i = l.search(/["“”]/);
  return i < 0 ? l : l.slice(0, i);
};

// -----------------------------------------------------------------------------
// Anchor extraction — ONLY the lines a setter actually says out loud. Two shapes
// count as spoken script:
//   1. A YOU:/LEAD: labeled line (the dialogue).
//   2. A "quoted string" anywhere (the docs always quote example scripts, incl.
//      the numbered question tables like `| 1 | "And how long..." |`).
// Everything else is COACHING or REFERENCE — coaching prose, the priority-order
// table, the "which doc am I in" table, section explainers. A setter never says
// those, so we must never anchor on them (else a click teleports to a comment).
// Headers and provenance comments are skipped outright.
//
// `closerOnly` (calrev) additionally drops every anchor attributed to the PROSPECT
// (see the speaker-aware note above). Default false = the original behavior, so the
// phone-setter dialview corpus is unchanged.
// -----------------------------------------------------------------------------
export function extractAnchors(scriptMd: string, file: string, closerOnly = false): Anchor[] {
  const anchors: Anchor[] = [];
  const seen = new Set<string>();
  const add = (fragment: string): void => {
    // Break on [PLACEHOLDER] slots and sentence ends → fixed phrases.
    for (const frag of fragment.split(/\[[^\]]*\]|[.?!]/)) {
      const tokens = normTokens(frag);
      if (tokens.length < MIN_ANCHOR_WORDS) continue;
      if (tokens.filter((t) => !STOP.has(t)).length < MIN_CONTENT_WORDS) continue;
      const phrase = tokens.join(" ");
      if (seen.has(phrase)) continue;
      seen.add(phrase);
      anchors.push({ tokens, file, phrase });
    }
  };
  let cue: "closer" | "lead" | "none" = "none"; // whose quote follows (closerOnly)
  for (const line of scriptMd.split("\n")) {
    if (/^\s*#{1,6}\s/.test(line)) {
      cue = "none";
      continue; // header
    }
    if (/^\s*<!--/.test(line)) continue; // provenance comment
    if (closerOnly && /^\s*$/.test(line)) {
      cue = "none"; // a blank line ends the current turn
      continue;
    }
    // 1) A dialogue-labeled line (YOU:/LEAD:/Voicemail:/Text:/NAME:) — take the
    //    whole line, labels stripped. In closerOnly, a LEAD: line is dropped and an
    //    ALLCAPS-colon label (SCRIPT:) sets the cue for the quote(s) that follow.
    if (isDialogueLabel(line)) {
      if (closerOnly) {
        if (DIA_LEAD.test(line)) {
          cue = "lead";
          continue; // drop the prospect's line
        }
        if (DIALOGUE_LABEL.test(line)) {
          cue = "closer"; // YOU:/Voicemail:/Text: — the closer's line
        } else if (LEAD_CUE.test(line)) {
          cue = "lead"; // an ALLCAPS-colon label that names the prospect
          continue;
        } else {
          cue = CLOSER_CUE.test(line) ? "closer" : "none";
        }
      }
      add(stripDialogueLabels(line));
      continue;
    }
    // 2) Quoted example script: a quote-dominant standalone line, OR a quoted
    //    cell in a table row (the numbered question tables). A short quote
    //    embedded in coaching prose is NEITHER, so it is skipped — that is what
    //    keeps a click off "…flat tone, \"I don't know\"…". Checked BEFORE the
    //    bold-header cue rule so a bold-WRAPPED quote inherits the header's cue.
    if (isQuoteDominant(line) || isTableRow(line)) {
      if (closerOnly) {
        const pre = preQuote(line);
        if (LEAD_INLINE.test(pre)) continue; // "Prospect says …" — drop
        if (!CLOSER_INLINE.test(pre) && cue === "lead") continue; // under a lead cue — drop
      }
      const quotes = line.replace(/[“”]/g, '"').match(/"([^"]+)"/g);
      if (quotes) for (const q of quotes) add(q.replace(/"/g, " "));
      continue;
    }
    // 3) (closerOnly) A mixed-case bold header with no quote (**What you say:**)
    //    sets the cue for the following quote lines; it is never anchored itself.
    if (closerOnly && BOLD_HEADER.test(line)) {
      cue = LEAD_CUE.test(line) ? "lead" : CLOSER_CUE.test(line) ? "closer" : "none";
      continue;
    }
  }
  return anchors;
}

// Generic conversational words that carry no script identity on their own. An
// anchor built only of STOP words + these is filler the course quotes as example
// dialogue ("what's going on", "does that make sense", "yeah that's exactly it").
// On a short dial it rarely re-collides, but on a 59-minute closer transcript it
// matches everywhere and teleports nowhere useful. filterDistinctiveAnchors uses
// this — the calrev course panel opts in; dialview keeps the full set (its short
// transcripts don't need it, and Mako's happy with it).
const GENERIC = new Set(
  "okay ok yeah yep yes no know think thing things time good great really want need going go guess sure mean means sense stuff kind sort right well now today feel felt make made let doing done say said talk talking little bit lot actually maybe probably stuck give given exactly confused alone able came come back look looking very much more most".split(
    " "
  )
);

// A DISTINCTIVE token carries script identity: longer than two chars, not a digit
// (amounts collide), not a function word (STOP), not generic chit-chat (GENERIC).
// This is the same test filterDistinctiveAnchors applies to whole anchors, reused
// at MATCH time (findHits, strictContent) so the edit budget can't substitute the
// one word that made the line distinctive — "you've seen the RESULTS" must not match
// "you've seen the VALUE" just because the debris token "ve" + "seen" already clear
// the content guard.
const isDistinctive = (t: string): boolean =>
  t.length > 2 && !/^\d+$/.test(t) && !STOP.has(t) && !GENERIC.has(t);

/** Keep only DISTINCTIVE anchors: at least `minWords` tokens AND at least
 *  `minDistinct` words that are neither function words (STOP) nor generic
 *  chit-chat (GENERIC) and longer than two characters. This is what separates a
 *  real script line ("why are you starting today and not last month") from
 *  example-dialogue filler ("i don't know") on a long transcript. Measured
 *  2026-09-18: on a FAIL call it drops 10 filler hits to 0; on a script-run call
 *  it keeps 8 real ones ("we don't work with everyone", "the investment is …"). */
export function filterDistinctiveAnchors(
  anchors: Anchor[],
  minWords = 5,
  minDistinct = 2
): Anchor[] {
  return anchors.filter((a) => {
    if (a.tokens.length < minWords) return false;
    // A DIGIT is not distinctive identity (see isDistinctive) — "the investment is
    // 5,000" would otherwise collide with every "$5,000" a lead or closer says.
    let d = 0;
    for (const t of a.tokens) if (isDistinctive(t)) d++;
    return d >= minDistinct;
  });
}

/** Merge many scripts' anchors into one deduped corpus (first file wins a tie).
 *  `closerOnly` (calrev) drops the prospect-attributed anchors; default false keeps
 *  the phone-setter dialview corpus byte-identical. */
export function mergeAnchors(
  entries: Array<{ file: string; text: string }>,
  closerOnly = false
): Anchor[] {
  const merged: Anchor[] = [];
  const seen = new Set<string>();
  for (const { file, text } of entries) {
    for (const a of extractAnchors(text, file, closerOnly)) {
      if (seen.has(a.phrase)) continue;
      seen.add(a.phrase);
      merged.push(a);
    }
  }
  return merged;
}

// Bounded prefix-aligned Levenshtein between anchor tokens and a transcript window.
// Allows INSERT / DELETE / SUBSTITUTE (STT adds and drops words, which the old
// fixed-offset compare could not absorb — one inserted filler word shifted every
// following token and blew the match). Returns the best full-anchor alignment
// { edits, consumed window length }, or null if it needs more than `budget` edits.
function boundedAlign(
  a: string[],
  win: string[],
  budget: number
): { edits: number; consumed: number } | null {
  const k = a.length;
  const W = win.length;
  let prev = new Array<number>(W + 1);
  for (let j = 0; j <= W; j++) prev[j] = j;
  for (let i = 1; i <= k; i++) {
    const cur = new Array<number>(W + 1);
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= W; j++) {
      const cost = a[i - 1] === win[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > budget) return null; // whole row already over budget → prune
    prev = cur;
  }
  let best: { edits: number; consumed: number } | null = null;
  for (let j = Math.max(0, k - budget); j <= Math.min(W, k + budget); j++) {
    if (prev[j] <= budget && (!best || prev[j] < best.edits))
      best = { edits: prev[j], consumed: j };
  }
  return best;
}

// Slide each anchor over a token stream; return hits with their anchor + span.
// Edit-distance match anchored on the anchor's first STRONG word (its handle), so
// a garbled/dropped OPENING word no longer kills a real line, while the handle +
// a ≥2 content-word guard keep a generic phrase from matching by luck (e.g. "want
// …" never satisfies a "drop …" anchor — the handle "drop" is absent).
//
// `strictContent` (calrev) tightens the ≥2 guard from "strong" (non-STOP/non-WEAK,
// which counts 2-char apostrophe debris like "ve") to DISTINCTIVE tokens only. That
// stops the edit budget from substituting the one word that made the line unique:
// "you've seen the results" no longer matches "you've seen the value". Default false
// keeps the phone-setter dialview matcher unchanged.
function findHits(
  tok: string[],
  anchors: Anchor[],
  strictContent = false
): Array<{ at: number; len: number; anchor: Anchor }> {
  const hits: Array<{ at: number; len: number; anchor: Anchor }> = [];
  for (const anchor of anchors) {
    const at = anchor.tokens;
    const k = at.length;
    if (k > tok.length) continue;
    const budget = budgetFor(k);
    // Handle = first non-stop token. Anchoring here (not on token 0) lets STT
    // drop/garble a leading filler word without losing the line.
    let lead = 0;
    while (lead < k && STOP.has(at[lead])) lead++;
    if (lead >= k) continue;
    const handle = at[lead];
    const content = new Set(
      at.filter(strictContent ? isDistinctive : (t) => !STOP.has(t) && !WEAK.has(t))
    );
    for (let p = 0; p < tok.length; p++) {
      if (tok[p] !== handle) continue;
      const off = Math.max(0, p - lead);
      const win = tok.slice(off, off + k + budget);
      const res = boundedAlign(at, win, budget);
      if (!res) continue;
      // ≥2 of the anchor's content words must actually appear in the window.
      const winSet = new Set(win);
      let cm = 0;
      for (const c of content) if (winSet.has(c)) cm++;
      if (cm < 2) continue;
      hits.push({ at: off, len: res.consumed, anchor });
      p = off + res.consumed - 1; // don't re-match inside this run
    }
  }
  return hits;
}

// Match against a word-timed transcript. Returns a map of WORD index → the
// anchor that covered it (for per-word highlight + click-to-teleport).
export function matchWords(words: WordTime[], anchors: Anchor[]): Map<number, Anchor> {
  const out = new Map<number, Anchor>();
  if (!anchors.length || !words.length) return out;
  const tok: string[] = [];
  const owner: number[] = [];
  words.forEach((w, i) => {
    for (const t of normTokens(w.word)) {
      tok.push(t);
      owner.push(i);
    }
  });
  for (const h of findHits(tok, anchors)) {
    for (let j = 0; j < h.len; j++) out.set(owner[h.at + j], h.anchor);
  }
  return out;
}

export interface FlatMatch {
  start: number;
  end: number;
  anchor: Anchor;
}

// Match against a flat transcript string. Returns char ranges with their anchor.
// `strictContent` (calrev) tightens the content guard — see findHits.
export function matchFlat(text: string, anchors: Anchor[], strictContent = false): FlatMatch[] {
  if (!anchors.length || !text) return [];
  const tok: string[] = [];
  const spanStart: number[] = [];
  const spanEnd: number[] = [];
  const re = /[A-Za-z0-9]+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    tok.push(canon(m[0].toLowerCase()));
    spanStart.push(m.index);
    spanEnd.push(m.index + m[0].length);
  }
  const matches: FlatMatch[] = findHits(tok, anchors, strictContent).map((h) => ({
    start: spanStart[h.at],
    end: spanEnd[h.at + h.len - 1],
    anchor: h.anchor,
  }));
  matches.sort((a, b) => a.start - b.start);
  // Drop ranges that overlap an earlier one (keep the earlier anchor).
  const kept: FlatMatch[] = [];
  for (const r of matches) {
    const last = kept[kept.length - 1];
    if (last && r.start < last.end) continue;
    kept.push(r);
  }
  return kept;
}
